import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import * as Icons from "lucide-react";
import Breadcrumb from "./Breadcrumb";

// Coquille de fiche façon SaaS (HubSpot/Pipedrive) — RÉUTILISABLE.
// props:
//  breadcrumb: [{ label, to? }] (le libellé de la vue active est ajouté automatiquement)
//  header: noeud React (photo, nom, fonction, coordonnées, actions rapides, KPI) — reste visible (sticky)
//  sections: [{ id, label, icon, render: () => JSX, badge? }] — badge : nombre à
//    afficher en pastille (masqué si falsy, donc 0/undefined n'affichent rien).
//    Motif emprunté à Layout.js#nav-badge, seuil resserré à 9 (Layout va à 99 —
//    au-delà, le chiffre exact n'apporte rien sur un compteur d'étapes en attente).
//  testid
export default function RecordShell({ breadcrumb = [], header, sections = [], testid = "record" }) {
  const valid = sections.filter(Boolean);
  const reduce = useReducedMotion();
  const [sp, setSp] = useSearchParams();
  const param = sp.get("vue");
  const active = valid.some((s) => s.id === param) ? param : valid[0]?.id;
  const current = valid.find((s) => s.id === active) || valid[0];

  // Chaque onglet ouvert est une étape de l'historique : « Retour » revient à l'onglet précédent, puis quitte la fiche
  // (recette du 15/10, LIM-03 ; décision de Mélilia, question 6). Jusque-là, l'onglet remplaçait l'adresse.
  const setActive = (id) => {
    if (id !== active) {
      const n = new URLSearchParams(sp);
      n.set("vue", id);
      setSp(n);
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const headerRef = useRef();
  const [hH, setHH] = useState(0);
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setHH(el.offsetHeight));
    ro.observe(el);
    setHH(el.offsetHeight);
    return () => ro.disconnect();
  }, []);

  const stickyTop = 64 + hH + 24; // top-nav (64) + header + marge

  // Téléphone et tablette : menu déroulant (décision F1 du 18/08) au lieu des onglets défilants.
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef();
  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setMenuOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [menuOpen]);
  const badgeOf = (it, suffix) => it.badge > 0 && (
    <span data-testid={`recordnav-${it.id}-badge${suffix}`}
      className="min-w-[18px] h-[18px] px-1 rounded-full bg-rouge text-white text-[10px] font-semibold flex items-center justify-center shrink-0">
      {it.badge > 9 ? "9+" : it.badge}
    </span>
  );

  // --record-sticky-top : là où le contenu passe sous le bandeau collant ; une colonne de phase s'y arrête (PhaseBoard).
  return (
    <div data-testid={testid} style={{ "--record-sticky-top": `${stickyTop}px` }}>
      <Breadcrumb items={[...breadcrumb, { label: current?.label }]} />

      <div ref={headerRef} className="sticky top-16 z-30 mb-6" data-testid={`${testid}-header`}>
        {header}
      </div>

      <div className="lg:flex lg:gap-8 items-start">
        <aside className="hidden lg:block w-56 shrink-0">
          <nav aria-label="Sections de la fiche" className="sticky space-y-1" style={{ top: stickyTop }} data-testid={`${testid}-nav`}>
            <div className="text-xs uppercase tracking-[0.2em] text-bordeaux font-semibold px-3 mb-3">Sur cette fiche</div>
            {valid.map((it) => {
              const on = it.id === active;
              return (
                <button key={it.id} data-testid={`recordnav-${it.id}`} onClick={() => setActive(it.id)}
                  className={`relative w-full flex items-center px-3.5 py-2.5 text-sm rounded-xl text-left transition-colors duration-200 ${
                    on ? "text-marine font-semibold" : "text-marine/80 hover:text-marine"
                  }`}>
                  {on && (
                    <motion.div layoutId={`${testid}-active-pill`} className="absolute inset-0 bg-white rounded-xl shadow-sm"
                      transition={reduce ? { duration: 0 } : { type: "tween", duration: 0.22, ease: [0.22, 1, 0.36, 1] }} />
                  )}
                  <span className="relative z-10 truncate">{it.label}</span>
                  {it.badge > 0 && (
                    <span data-testid={`recordnav-${it.id}-badge`}
                      className="relative z-10 ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-rouge text-white text-[10px] font-semibold flex items-center justify-center shrink-0">
                      {it.badge > 9 ? "9+" : it.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </aside>

        <div className="flex-1 min-w-0">
          {/* Téléphone et tablette : menu déroulant. Le bouton n'a pas de libellé visible (le titre de la fiche
              est juste au-dessus) : aria-label « Navigation de la fiche », comme décidé sur l'ébauche du 18/08. */}
          <div ref={menuRef} className="lg:hidden relative mb-4">
            <button type="button" onClick={() => setMenuOpen((o) => !o)} aria-label="Navigation de la fiche" aria-expanded={menuOpen}
              aria-describedby={`${testid}-menu-current`} data-testid={`${testid}-menu-button`}
              className="w-full flex items-center gap-2 px-4 py-3 rounded-sm bg-white border border-marine/20 text-sm font-semibold text-marine text-left">
              <span id={`${testid}-menu-current`} className="truncate">{current?.label}</span>
              {current && badgeOf(current, "-current")}
              <Icons.ChevronDown size={16} className={`ml-auto shrink-0 transition-transform ${menuOpen ? "rotate-180" : ""}`} aria-hidden="true" />
            </button>
            {menuOpen && (
              <div className="absolute left-0 right-0 top-full mt-1 z-40 bg-white rounded-sm border border-marine/10 shadow-xl py-1.5 max-h-[60vh] overflow-y-auto" data-testid={`${testid}-menu`}>
                {valid.map((it) => {
                  const on = it.id === active;
                  return (
                    <button key={it.id} type="button" data-testid={`recordnav-${it.id}-mobile`} aria-current={on ? "page" : undefined}
                      onClick={() => { setActive(it.id); setMenuOpen(false); }}
                      className={`w-full flex items-center gap-2 px-4 py-2.5 text-sm text-left transition-colors ${on ? "text-marine font-semibold bg-marine-50" : "text-marine hover:bg-sable-clair"}`}>
                      <span className="truncate">{it.label}</span>
                      <span className="ml-auto flex items-center gap-2 shrink-0">
                        {badgeOf(it, "-mobile")}
                        {on && <Icons.Check size={16} aria-hidden="true" />}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <div data-testid={`${testid}-content`}>{current?.render()}</div>
        </div>
      </div>
    </div>
  );
}
